/**
 * #605 — Papan's own picker for an app's `papan.files.pickAndSave`: the person
 * chooses where the app's file goes, and under what name — or, for several
 * files, the folder they all go in (each keeps its own name). Rendered by the
 * picker PAGE (`/__handoff/<pid>`) — a modal iframe in Papan, a popup on
 * pd.app — never in the app's document.
 *
 * What the picker shows comes from the server, and is only a hint — the
 * answer and the upload each re-check everything:
 *   - which folders take a save (`saveTargets`): a folder the person can't
 *     save into stays openable when it leads to one they can, and is dimmed
 *     with the reason when it doesn't;
 *   - what the typed name would do (`probeName`): free; an existing file they
 *     may replace — asked first, OS-style, never silently; or blocked (one
 *     answer for "can't replace it" and "can't see it").
 * After the answer the picker stays, following the save to its end — the
 * PERSON learns where the file went; the app only learns saved / not saved.
 *
 * UX rules: docs/plans/2026-09-18_PDAPP_FILE_HANDOFF.md §9.1.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import {
  formatStorageBytes,
  isLiveMountRow,
  matchesHandoffFilter,
  type HandoffNameProbe,
  type HandoffRequestView,
  type HandoffSaveFailure,
  type HandoffSaveRefusal,
  type HandoffSaveStatus,
  type HandoffSaveTarget,
} from '@papan/types'
import type { FileListItem } from '@papan/files/client'
import { handoffApi } from '@papan/files/client/handoff'
import { useArmed } from './useArmed'
import { HandoffHeader as Header } from './HandoffHeader'
import { useTypingCarry } from './useTypingCarry'
import { CheckFailed, PHONE_BAR, PHONE_BUTTON, answeredAfterAll, retryDelay, usePhoneBar } from './HandoffPicker'
import {
  PickerNavigator,
  getRootFolderId,
  readableName,
  type PickerCrumb,
  type PickerNavigatorHandle,
  type PickerSelection,
  type RowDecoration,
} from '../PickerNavigator'

/** A save request, as the picker page describes it. */
export type SaveRequestView = Extract<HandoffRequestView, { kind: 'save' }>

/** Permissions that may write a file — the listing's hint for file rows. */
const WRITE_PERMS = new Set(['readwrite', 'admin', 'owner'])
/** How many rows one save-targets call asks about (the server's own cap). */
const TARGETS_BATCH = 200
/** How often the picker asks how the save stands once answered. */
const STATUS_POLL_MS = 1_000
/** How long "Saved" stays up before the picker closes itself. */
const SAVED_LINGER_MS = 2_000

export interface HandoffSavePickerProps {
  pid: string
  view: SaveRequestView
  tenantId: string
  headerAside?: ReactNode
  initialFolderId?: string | null
  onFolderChange?: (folderId: string) => void
  /** `answered`: the person chose — the host may hand the app its upload URL
   *  (the picker stays, following the save). `cancelled`: they gave up.
   *  `closed`: they are done watching the save. */
  onFinished: (status: 'answered' | 'cancelled' | 'closed') => void
}

type Phase =
  | { kind: 'choosing' }
  | { kind: 'following'; folder: string; name: string; count: number; status: HandoffSaveStatus | null }
  | { kind: 'gone' }

/** `busy`: rate-limited — Save stays, to be pressed again in a moment.
 *  `failed`: lost on the way (the network, a hiccup) — try again. */
type Refusal = HandoffSaveRefusal | 'unavailable' | 'busy' | 'failed'

type T = (key: string, opts?: Record<string, unknown>) => string

/** The save picker's title (and the page's): who asks, for what, how big. */
export function saveTitle(view: SaveRequestView, t: T): string {
  const app = view.appName || t('handoff.anApp')
  const size = formatStorageBytes(view.save.size)
  if (view.save.files) return t('handoff.title.saveMany', { app, n: view.save.files.length, count: view.save.files.length, size })
  return view.save.select === 'file'
    ? t('handoff.title.saveOver', { app, size })
    : t('handoff.title.save', { app, name: view.save.suggestedName, size })
}

export function HandoffSavePicker({ pid, view, tenantId, headerAside, initialFolderId, onFolderChange, onFinished }: HandoffSavePickerProps) {
  const { t } = useTranslation('files')
  const { save } = view
  const app = view.appName || t('handoff.anApp')
  const rootId = getRootFolderId(tenantId)
  const roots = useMemo(() => [{ id: rootId, label: 'papan' }], [rootId])
  const replaceOnly = save.select === 'file'
  // Several files: the person chooses a folder; each keeps its own name.
  const many = save.files ?? null
  const navigatorRef = useRef<PickerNavigatorHandle>(null)

  const [phase, setPhase] = useState<Phase>({ kind: 'choosing' })
  const [folder, setFolder] = useState<{ id: string; name: string } | null>(null)
  const [name, setName] = useState(replaceOnly ? '' : save.suggestedName)
  /** `retrying`: the check failed on the way (network, a hiccup) — asked again
   *  shortly. `checkFailed`: in a way asking again by itself would not mend —
   *  the person may try again. */
  const [probe, setProbe] = useState<HandoffNameProbe | 'checking' | 'retrying' | 'checkFailed' | null>(null)
  const [probeRetry, setProbeRetry] = useState(0)
  /** The person's "Try again": a fresh check, not a retry in a row. */
  const [probeAgain, setProbeAgain] = useState(0)
  // Which retry the last probe was, and how many in a row have failed — a
  // new name or folder starts afresh (and says "checking", not "trying again").
  const probeRetrySeen = useRef(0)
  const probeFailures = useRef(0)
  // Several files: how many of their names are already in the folder.
  const [replaceCount, setReplaceCount] = useState(0)
  const [refusal, setRefusal] = useState<Refusal | null>(null)
  const [confirmingReplace, setConfirmingReplace] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  // The person did something HERE (typed a name, clicked, navigated): until
  // then, Enter in the name field — focused and filled in by the app — does
  // not answer for them. And while typing carried over from the app goes on
  // (a burst already going when the picker appeared), no key answers at all.
  const [acted, setActed] = useState(false)
  const carry = useTypingCarry()
  const armed = useArmed()
  const [targets, setTargets] = useState<ReadonlyMap<string, HandoffSaveTarget>>(() => new Map())
  const requested = useRef(new Set<string>())

  const markGone = useCallback(() => setPhase({ kind: 'gone' }), [])
  const onError = useCallback((err: unknown) => {
    if ((err as { status?: number })?.status === 409) markGone()
  }, [markGone])

  // ── Which folders take a save ────────────────────────────────────────────
  // Asks once per id; a failed lookup is forgotten, so the next showing retries it.
  const [targetsRetry, setTargetsRetry] = useState<{ n: number; afterMs: number } | null>(null)
  /** The lookup's own trouble: `retrying` (backing off by itself) or
   *  `failed` (a failure asking again would not mend — the person may). */
  const [targetsTrouble, setTargetsTrouble] = useState<'retrying' | 'failed' | null>(null)
  const targetsFailures = useRef(0)
  /** A Cancel that never reached the server — the picker stays, and says so. */
  const [cancelFailed, setCancelFailed] = useState(false)
  /** The person can save nowhere (a read-only account), from the lookup. */
  const [accountRefused, setAccountRefused] = useState<'read_only_account' | null>(null)
  const learnTargets = useCallback((ids: string[]) => {
    const fresh = [...new Set(ids)].filter(id => !requested.current.has(id))
    for (let i = 0; i < fresh.length; i += TARGETS_BATCH) {
      const batch = fresh.slice(i, i + TARGETS_BATCH)
      for (const id of batch) requested.current.add(id)
      handoffApi.saveTargets(pid, batch).then(
        (res) => {
          targetsFailures.current = 0
          setTargetsTrouble(null)
          if (res.refused) setAccountRefused(res.refused)
          setTargets(prev => new Map([...prev, ...Object.entries(res.rows)]))
        },
        (err) => {
          for (const id of batch) requested.current.delete(id)
          onError(err)
          const { status } = (err ?? {}) as { status?: number }
          if (status === 409) return
          // As the name probe: back off by itself on the network, a hiccup or
          // a rate limit; otherwise leave it to the person's "Try again".
          const afterMs = retryDelay(err as { status?: number; retryAfterSec?: number }, ++targetsFailures.current)
          if (afterMs === null) { setTargetsTrouble('failed'); return }
          setTargetsTrouble('retrying')
          setTargetsRetry(r => ({ n: (r?.n ?? 0) + 1, afterMs }))
        },
      )
    }
  }, [pid, onError])

  const onItemsShown = useCallback((items: FileListItem[]) => {
    learnTargets(items.filter(i => i.isFolder).map(i => i.id))
  }, [learnTargets])

  // The navigator reports the folder it opened in; a report of ANOTHER folder
  // after that is the person navigating (the same folder re-reported — its
  // path re-told as the navigator settles — is not).
  const reportedRef = useRef<{ folderId: string | null } | null>(null)
  const onNavigate = useCallback((folderId: string | null, crumbs: PickerCrumb[]) => {
    // A cancel that did not land is news until the person carries on: moving,
    // choosing or renaming is choosing to save, and the line is theirs again.
    if (reportedRef.current && reportedRef.current.folderId !== folderId) { setActed(true); setCancelFailed(false) }
    reportedRef.current = { folderId }
    setFolder(folderId ? { id: folderId, name: readableName(crumbs[crumbs.length - 1]?.name ?? '') } : null)
    setConfirmingReplace(false)
    setRefusal(null)
    if (folderId) {
      onFolderChange?.(folderId)
      learnTargets([folderId])
    }
  }, [onFolderChange, learnTargets])

  const folderTarget = folder ? targets.get(folder.id) : undefined

  // The open folder's lookup failed (a blip, a 429): ask again while the
  // person stays there — else Save waits on "Checking…" until they leave.
  const openId = folder?.id
  const choosing = phase.kind === 'choosing'
  useEffect(() => {
    if (!targetsRetry || !openId || !choosing) return
    const timer = setTimeout(() => learnTargets([openId]), targetsRetry.afterMs)
    return () => clearTimeout(timer)
  }, [targetsRetry, openId, choosing, learnTargets])

  const decorateItem = useCallback((item: FileListItem): RowDecoration | undefined => {
    if (item.isFolder) {
      const target = targets.get(item.id)
      if (target === 'none') return { dimReason: t('handoff.save.reason.cantSaveHere') }
      if (target === 'hallway') return { readOnly: true }
      return undefined
    }
    // A link row is not a file to replace — its target lives elsewhere (the
    // server refuses it; say why here).
    if (isLiveMountRow(item.name, item.fileType)) return { dimReason: t('handoff.save.reason.link') }
    if (!matchesHandoffFilter({ name: item.name, mime: item.mimeType }, save.filter)) {
      return { dimReason: t('handoff.save.reason.notThisKind', { app }) }
    }
    const perm = item.effectivePermission ?? item.permission
    return perm && !WRITE_PERMS.has(perm) ? { readOnly: true } : undefined
  }, [targets, save.filter, app, t])

  // A file clicked: its name is the one to save under (a replace, asked first).
  const onSelect = useCallback((sel: PickerSelection) => {
    if (sel.isFolder || isLiveMountRow(sel.name, sel.raw.fileType)) return
    if (!matchesHandoffFilter({ name: sel.name, mime: sel.raw.mimeType }, save.filter)) return
    setActed(true)
    setCancelFailed(false)
    // The destination is always the folder being browsed: a file picked
    // elsewhere (the parent pane, a search hit) takes the person there.
    if (sel.parentId && sel.parentId !== navigatorRef.current?.currentFolderId) navigatorRef.current?.navigateTo(sel.parentId)
    setName(sel.name)
  }, [save.filter])

  // ── What the name would do here ──────────────────────────────────────────
  /** Several files: one answer for all — any name that can't go here holds
   *  all of them back; names already here are asked about together, by count. */
  const probeMany = useCallback((folderId: string) => handoffApi.probeNames(pid, folderId, (many ?? []).map(f => f.name)).then((res) => {
    const replaceable = res.names.filter(n => n === 'replaceable').length
    const probe: HandoffNameProbe = res.names.includes('invalid') ? 'invalid'
      : res.names.includes('blocked') ? 'blocked'
        : replaceable > 0 ? 'replaceable' : 'free'
    return { probe, replaceable }
  }), [pid, many])

  const probeSeq = useRef(0)
  useEffect(() => {
    const seq = ++probeSeq.current
    setConfirmingReplace(false)
    setRefusal(null)
    // Only a folder that takes a save is asked about: one the person is just
    // passing through (or can't save in) has nothing to say about the names.
    if (phase.kind !== 'choosing' || !folder || folderTarget !== 'selectable' || (!many && !name.trim())) { setProbe(null); return }
    const isRetry = probeRetry !== probeRetrySeen.current
    probeRetrySeen.current = probeRetry
    if (!isRetry) probeFailures.current = 0
    setProbe(p => (isRetry && p === 'retrying' ? p : 'checking'))
    let retry: ReturnType<typeof setTimeout> | undefined
    const timer = setTimeout(() => {
      const probed = many
        ? probeMany(folder.id)
        : handoffApi.probeName(pid, folder.id, name.trim()).then(res => ({ probe: res.name, replaceable: 0 }))
      probed.then(
        // Only the latest folder's answer: a slow one for a folder left since
        // must not set the count the "Replace?" question states.
        (res) => { if (seq === probeSeq.current) { setProbe(res.probe); setReplaceCount(res.replaceable) } },
        (err: { status?: number; retryAfterSec?: number }) => {
          if (seq !== probeSeq.current) return
          if (err?.status === 409) { setProbe(null); onError(err); return }
          // Not an answer about the name: say so, and ask again, backing off;
          // a failure that would answer the same is left to the person to try again.
          const wait = retryDelay(err, ++probeFailures.current)
          if (wait === null) { setProbe('checkFailed'); return }
          setProbe('retrying')
          retry = setTimeout(() => setProbeRetry(n => n + 1), wait)
        },
      )
    }, 250)
    return () => { clearTimeout(timer); clearTimeout(retry) }
  }, [pid, folder, folderTarget, name, phase.kind, onError, many, probeMany, probeRetry, probeAgain])

  const trimmed = name.trim()
  const folderTakesSave = folderTarget === 'selectable'
  const canSubmit = armed && phase.kind === 'choosing' && !!folder && folderTakesSave && (!!many || !!trimmed) && !submitting
    && (probe === 'free' || probe === 'replaceable') && (!replaceOnly || probe === 'replaceable')

  const answer = useCallback(async (replace: boolean) => {
    if (!folder || (!many && !trimmed)) return
    setSubmitting(true)
    setRefusal(null)
    setCancelFailed(false)
    try {
      const res = await handoffApi.answerSave(pid, many ? { parentId: folder.id, replace } : { parentId: folder.id, name: trimmed, replace })
      if (res.ok) {
        setPhase({ kind: 'following', folder: folder.name, name: trimmed, count: many?.length ?? 1, status: null })
        onFinished('answered')
        return
      }
      if (res.reason === 'exists') {
        // Taken since the name was checked: ask, as for any existing file —
        // several files: counted again first, so the question states how many
        // "Replace" would replace.
        if (many) {
          const fresh = await probeMany(folder.id)
          setProbe(fresh.probe)
          setReplaceCount(fresh.replaceable)
          if (fresh.probe === 'replaceable') setConfirmingReplace(true)
        } else {
          setProbe('replaceable')
          setConfirmingReplace(true)
        }
      } else {
        setRefusal(res.reason)
      }
    } catch (err) {
      // Lost on the way — or refused as already settled, after an earlier try
      // whose reply was lost: the answer may have landed. If it did, it stands
      // — follow the save rather than say it failed while the app uploads.
      if (await answeredAfterAll(pid)) {
        setPhase({ kind: 'following', folder: folder.name, name: trimmed, count: many?.length ?? 1, status: null })
        onFinished('answered')
      } else if ((err as { status?: number })?.status === 409) markGone()
      else setRefusal((err as { status?: number })?.status === 429 ? 'busy' : 'failed')
    } finally {
      setSubmitting(false)
    }
  }, [pid, folder, trimmed, onFinished, markGone, many, probeMany])

  const submit = useCallback(() => {
    // Keys carried over from the app — Enter in the name field, or Tab onto
    // this button and Space — are not the person answering. (A click is: the
    // pointer press ends the carry first.)
    if (!canSubmit || carry.carrying()) return
    // Replacing is never silent: the person says so first — unless the only
    // thing this app may do is replace, and the button already says "Replace".
    if (probe === 'replaceable' && !replaceOnly && !confirmingReplace) { setConfirmingReplace(true); return }
    void answer(probe === 'replaceable')
  }, [canSubmit, carry, probe, replaceOnly, confirmingReplace, answer])

  const cancel = useCallback(() => {
    // Report it once the server has it — the host reads the outcome at once.
    // A cancel that never landed keeps the picker, and says so (a state of its
    // own: the name check's refreshes must not wipe it).
    setCancelFailed(false)
    void handoffApi.cancel(pid).then((landed) => { if (landed) onFinished('cancelled'); else setCancelFailed(true) })
  }, [pid, onFinished])

  // ── After the answer: follow the save ────────────────────────────────────
  const following = phase.kind === 'following'
  useEffect(() => {
    if (!following) return
    let stopped = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let failures = 0
    const poll = () => {
      handoffApi.saveStatus(pid).then(
        (status) => {
          if (stopped) return
          failures = 0
          setPhase(p => (p.kind === 'following' ? { ...p, status } : p))
          if (status.state === 'waiting' || status.state === 'saving') timer = setTimeout(poll, STATUS_POLL_MS)
        },
        (err: { status?: number; retryAfterSec?: number }) => {
          if (stopped) return
          // Only "not found" means there is nothing to follow any more — the
          // save's time ran out before it finished (a saved one is read as
          // saved before then): it was not saved, and the person is told so,
          // not that the request "expired or was answered". A blip, a busy
          // server or a rate limit is asked again, later.
          if (err?.status === 404) {
            setPhase(p => (p.kind === 'following' && p.status?.state !== 'saved' && p.status?.state !== 'failed'
              ? { ...p, status: { state: 'failed', reason: 'error' } }
              : p))
            return
          }
          failures++
          const wait = err?.retryAfterSec ? err.retryAfterSec * 1000 : Math.min(STATUS_POLL_MS * 2 ** failures, 15_000)
          timer = setTimeout(poll, wait)
        },
      )
    }
    poll()
    return () => { stopped = true; clearTimeout(timer) }
  }, [following, pid])

  const saved = phase.kind === 'following' && phase.status?.state === 'saved'
  useEffect(() => {
    if (!saved) return
    const timer = setTimeout(() => onFinished('closed'), SAVED_LINGER_MS)
    return () => clearTimeout(timer)
  }, [saved, onFinished])

  // The button a key pressed is swapped out as the replace question comes and
  // goes — focus lands on its counterpart, never lost to the page. Asked, it
  // goes to "not this" (a second Enter must not replace): Replace is one Tab on.
  // Only when focus was IN the buttons: focus lost from anywhere else (a row
  // the navigator took away) is not handed to Save.
  const saveButtonRef = useRef<HTMLButtonElement>(null)
  const keepButtonRef = useRef<HTMLButtonElement>(null)
  const inButtons = useRef(false)
  const phoneBar = usePhoneBar()
  const primaryShape = phoneBar ? PHONE_BUTTON : 'px-3 py-1.5 rounded'
  const secondaryShape = phoneBar ? `${PHONE_BUTTON} themed-bg-tertiary` : 'px-3 py-1.5 rounded'
  const wasConfirming = useRef(confirmingReplace)
  useEffect(() => {
    if (wasConfirming.current === confirmingReplace) return
    wasConfirming.current = confirmingReplace
    const focused = document.activeElement
    const lost = !focused || focused === document.body || !focused.isConnected
    if (inButtons.current && lost) (confirmingReplace ? keepButtonRef : saveButtonRef).current?.focus()
  }, [confirmingReplace])

  // Escape: gives the request up while choosing (the search field and the
  // replace question keep Escape for themselves first); closes afterwards.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      if (confirmingReplace) { setConfirmingReplace(false); return }
      if (phase.kind === 'choosing') cancel()
      else onFinished('closed')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [confirmingReplace, phase.kind, cancel, onFinished])

  const title = saveTitle(view, t)

  if (phase.kind !== 'choosing') {
    return (
      <div className="flex flex-col h-full min-h-0" data-testid="handoff-save-picker">
        <Header title={title} aside={null} />
        <FollowSave phase={phase} app={app} onClose={() => onFinished('closed')} />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="handoff-save-picker" onPointerDownCapture={() => setActed(true)}>
      <Header title={title} subtitle={t(many ? 'handoff.save.subtitleMany' : replaceOnly ? 'handoff.save.subtitleReplace' : 'handoff.save.subtitle', { app })} aside={headerAside} />

      <PickerNavigator
        ref={navigatorRef}
        roots={roots}
        initialFolderId={initialFolderId ?? rootId}
        mode={save.select === 'folder' ? 'folder' : 'fileOrFolder'}
        selectedId={null}
        onSelect={onSelect}
        onConfirm={(sel) => { onSelect(sel) }}
        onNavigate={onNavigate}
        // A folder whose listing failed: the save has no destination until one
        // loads — never the folder the person left.
        onNavigateFailed={() => { setFolder(null); setConfirmingReplace(false) }}
        onItemsShown={onItemsShown}
        decorateItem={decorateItem}
        autoFocusSearch={false}
        className="flex-1"
      />

      <footer className="border-t themed-border themed-bg-tertiary">
        {many ? (
          // Every name, in full — the person cannot rename these, so none may
          // hide behind an ellipsis or a long neighbour; scrolls past ~3 lines.
          // Each name keeps its extension in view as a part of its own: a long
          // name is cut in its stem, never in what it will be saved as.
          <p className="mx-4 mt-3 max-h-[3.75rem] overflow-y-auto text-xs themed-text-secondary" data-testid="handoff-save-files">
            {t('handoff.save.filesLabel')}{' '}
            {many.map((f, i) => {
              const dot = f.name.lastIndexOf('.')
              const [stem, ext] = dot > 0 ? [f.name.slice(0, dot), f.name.slice(dot)] : [f.name, '']
              return (
                <span key={i} className="inline-flex max-w-full align-bottom" title={f.name}>
                  <span className="truncate">{stem}</span>
                  <span className="shrink-0">{ext}{i < many.length - 1 ? ',' : ''}</span>
                  {i < many.length - 1 && <span className="shrink-0">&nbsp;</span>}
                </span>
              )
            })}
          </p>
        ) : (
          <div className="flex items-center gap-2 px-4 pt-3">
            <label htmlFor="handoff-save-name" className="text-xs themed-text-secondary shrink-0">{t('handoff.save.name')}</label>
            <input
              id="handoff-save-name"
              data-testid="handoff-save-name"
              type="text"
              value={name}
              readOnly={replaceOnly}
              placeholder={replaceOnly ? t('handoff.save.chooseFileToReplace') : undefined}
              onChange={(e) => { setActed(true); setCancelFailed(false); setName(e.target.value) }}
              // Enter saves a choice the person made here — never a held key,
              // never before they did anything, never while typing carried over
              // from the app goes on (`submit`), and never the "Replace?"
              // question (that takes the Replace button). (…nor the Enter that
              // commits an IME composition.)
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.repeat && !e.nativeEvent.isComposing && acted && !confirmingReplace) submit()
              }}
              autoFocus={!replaceOnly}
              onFocus={(e) => {
                // Select the stem, so typing renames without losing the extension.
                const dot = e.currentTarget.value.lastIndexOf('.')
                e.currentTarget.setSelectionRange(0, dot > 0 ? dot : e.currentTarget.value.length)
              }}
              className="flex-1 min-w-0 text-sm themed-bg-secondary themed-text rounded px-2.5 py-1.5 border themed-border outline-none focus:border-brand-500 transition-colors"
            />
          </div>
        )}
        {/* A phone: the line on its own, then the two buttons across the width
            (as the read picker's bar); it clears the home indicator. */}
        <div
          className={phoneBar ? PHONE_BAR : 'flex items-center gap-3 px-4 py-3'}
          onFocus={() => { inButtons.current = true }}
          // A focused button REMOVED blurs with nowhere to go: still "in".
          onBlur={(e) => { if (e.relatedTarget) inButtons.current = false }}
        >
          <div className={`min-w-0 text-xs ${phoneBar ? 'col-span-2' : 'flex-1'}`} aria-live="polite" data-testid="handoff-save-status">
            <ChoosingStatus
              folder={folder}
              folderTarget={folderTarget}
              name={trimmed}
              probe={probe}
              refusal={refusal}
              confirmingReplace={confirmingReplace}
              replaceOnly={replaceOnly}
              many={many?.length ?? null}
              replaceCount={replaceCount}
              onTryAgain={() => setProbeAgain(n => n + 1)}
              accountRefused={accountRefused}
              cancelFailed={cancelFailed}
              targetsTrouble={targetsTrouble}
              onTryAgainTargets={() => {
                targetsFailures.current = 0
                setTargetsTrouble(null)
                if (openId) learnTargets([openId])
              }}
            />
          </div>
          {/* Keyed: each button is its own element — never the question's
              "not this" reused as the request's Cancel under a focused key. */}
          {confirmingReplace ? (
            <>
              <button key="keep" ref={keepButtonRef} type="button" data-testid="handoff-save-keep" onClick={() => setConfirmingReplace(false)} className={`text-sm themed-text-secondary hover:themed-text transition-colors ${secondaryShape}`}>
                {t('handoff.cancel')}
              </button>
              <ReplaceAnswer key="replace" submitting={submitting} onReplace={() => void answer(true)} label={t('handoff.save.replace')} shape={primaryShape} />
            </>
          ) : (
            <>
              <button key="cancel" type="button" onClick={cancel} className={`text-sm themed-text-secondary hover:themed-text transition-colors ${secondaryShape}`}>
                {t('handoff.cancel')}
              </button>
              <button
                key="save"
                ref={saveButtonRef}
                type="button"
                data-testid="handoff-confirm"
                onClick={submit}
                disabled={!canSubmit}
                className={`flex items-center gap-1.5 text-sm themed-btn-primary transition-colors disabled:opacity-50 ${primaryShape}`}
              >
                {submitting && <Loader2 size={13} className="animate-spin" />}
                {probe === 'replaceable' ? t('handoff.save.replace') : many ? t('handoff.save.saveMany', { n: many.length, count: many.length }) : t('handoff.save.save')}
              </button>
            </>
          )}
        </div>
      </footer>
    </div>
  )
}


/** The footer's one line: where it will go, or why it can't yet. */
function ChoosingStatus({ folder, folderTarget, name, probe, refusal, confirmingReplace, replaceOnly, many, replaceCount, onTryAgain, accountRefused, cancelFailed, targetsTrouble, onTryAgainTargets }: {
  folder: { id: string; name: string } | null
  folderTarget: HandoffSaveTarget | undefined
  name: string
  probe: HandoffNameProbe | 'checking' | 'retrying' | 'checkFailed' | null
  refusal: Refusal | null
  confirmingReplace: boolean
  replaceOnly: boolean
  /** Several files: how many (null for one). */
  many: number | null
  replaceCount: number
  onTryAgain: () => void
  accountRefused: 'read_only_account' | null
  cancelFailed: boolean
  targetsTrouble: 'retrying' | 'failed' | null
  onTryAgainTargets: () => void
}) {
  const { t } = useTranslation('files')
  if (cancelFailed) return <span className="text-red-500">{t('handoff.sendFailed')}</span>
  if (accountRefused) return <span className="text-red-500">{t(`handoff.save.refused.${accountRefused}`)}</span>
  if (probe === 'checkFailed') return <CheckFailed onTryAgain={onTryAgain} />
  if (refusal === 'busy') return <span className="text-amber-500">{t('handoff.busy')}</span>
  if (refusal === 'failed') return <span className="text-red-500">{t('handoff.sendFailed')}</span>
  if (refusal) return <span className="text-red-500">{t(`handoff.save.refused.${refusal}`)}</span>
  if (!folder) return <span className="themed-text-muted">{t('handoff.save.openFolder')}</span>
  if (folderTarget === undefined) {
    if (targetsTrouble === 'failed') return <CheckFailed onTryAgain={onTryAgainTargets} />
    if (targetsTrouble === 'retrying') return <span className="text-amber-500">{t('handoff.checkRetrying')}</span>
    return <span className="themed-text-muted">{t('handoff.measuring')}</span>
  }
  if (folderTarget !== 'selectable') return <span className="text-amber-500">{t('handoff.save.cantSaveInFolder')}</span>
  if (many !== null) {
    if (confirmingReplace) return <span className="text-amber-500">{t('handoff.save.confirmReplaceMany', { count: replaceCount, folder: folder.name })}</span>
    switch (probe) {
      case 'checking': return <span className="themed-text-muted">{t('handoff.measuring')}</span>
      case 'retrying': return <span className="text-amber-500">{t('handoff.checkRetrying')}</span>
      case 'invalid': return <span className="text-red-500">{t('handoff.save.invalidMany')}</span>
      case 'blocked': return <span className="text-red-500">{t('handoff.save.blockedMany')}</span>
      case 'replaceable': return <span className="text-amber-500">{t('handoff.save.existsMany', { count: replaceCount, folder: folder.name })}</span>
      case 'free': return <span className="themed-text-secondary">{t('handoff.save.willSaveMany', { n: many, count: many, folder: folder.name })}</span>
      default: return null
    }
  }
  if (confirmingReplace) {
    return <span className="text-amber-500">{t('handoff.save.confirmReplace', { name, folder: folder.name })}</span>
  }
  if (!name) {
    return <span className="themed-text-muted">{replaceOnly ? t('handoff.save.chooseFileToReplace') : t('handoff.save.typeName')}</span>
  }
  switch (probe) {
    case 'checking': return <span className="themed-text-muted">{t('handoff.measuring')}</span>
    case 'retrying': return <span className="text-amber-500">{t('handoff.checkRetrying')}</span>
    case 'invalid': return <span className="text-red-500">{t('handoff.save.refused.invalid_name')}</span>
    case 'blocked': return <span className="text-red-500">{t('handoff.save.blockedName')}</span>
    case 'replaceable':
      return replaceOnly
        ? <span className="themed-text-secondary">{t('handoff.save.willReplace', { name, folder: folder.name })}</span>
        : <span className="text-amber-500">{t('handoff.save.exists', { name })}</span>
    case 'free':
      return replaceOnly
        ? <span className="text-red-500">{t('handoff.save.chooseFileToReplace')}</span>
        : <span className="themed-text-secondary">{t('handoff.save.willSave', { name, folder: folder.name })}</span>
    default: return null
  }
}

/** After the answer: the save as the person sees it, to its end. */
function FollowSave({ phase, app, onClose }: { phase: Exclude<Phase, { kind: 'choosing' }>; app: string; onClose: () => void }) {
  const { t } = useTranslation('files')
  const status = phase.kind === 'following' ? phase.status : null
  let icon: ReactNode = <Loader2 size={28} className="animate-spin themed-text-muted" />
  let headline: string
  let detail: string | null = null
  if (phase.kind === 'gone') {
    icon = <AlertCircle size={28} className="themed-text-muted" />
    headline = t('handoff.gone')
  } else if (status?.state === 'saved') {
    icon = <CheckCircle2 size={28} className="text-green-500" />
    headline = phase.count > 1
      ? t('handoff.save.savedMany', { n: phase.count, count: phase.count, folder: phase.folder })
      : t('handoff.save.saved', { name: phase.name, folder: phase.folder })
  } else if (status?.state === 'failed') {
    icon = <AlertCircle size={28} className="text-red-500" />
    headline = phase.count > 1
      ? t('handoff.save.notSavedMany', { saved: status.count?.saved ?? 0, n: phase.count, count: phase.count })
      : t('handoff.save.notSaved', { name: phase.name })
    detail = t(`handoff.save.failed.${status.reason satisfies HandoffSaveFailure}`, { app, folder: phase.folder })
  } else if (status?.state === 'saving') {
    headline = phase.count > 1
      ? t('handoff.save.savingMany', { saved: status.count?.saved ?? 0, n: phase.count, count: phase.count, folder: phase.folder })
      : t('handoff.save.saving', { name: phase.name, folder: phase.folder })
  } else {
    headline = t(phase.kind === 'following' && phase.count > 1 ? 'handoff.save.waitingMany' : 'handoff.save.waiting', { app })
  }
  const finished = phase.kind === 'gone' || status?.state === 'saved' || status?.state === 'failed'
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center" aria-live="polite" data-testid="handoff-save-follow">
      {icon}
      <p className="text-sm themed-text">{headline}</p>
      {detail && <p className="text-xs themed-text-muted max-w-sm">{detail}</p>}
      <button
        type="button"
        data-testid="handoff-save-close"
        onClick={onClose}
        className={`mt-1 px-3 py-1.5 text-sm rounded transition-colors ${finished ? 'themed-btn-primary' : 'themed-text-secondary hover:themed-text'}`}
      >
        {finished ? t('handoff.save.done') : t('handoff.save.hide')}
      </button>
    </div>
  )
}

/**
 * The "Replace?" question's answer — a control of its OWN, armed when the
 * question appears (not when the picker did): the click or held Enter that
 * asked must not also answer it.
 */
function ReplaceAnswer({ submitting, onReplace, label, shape }: { submitting: boolean; onReplace: () => void; label: string; shape: string }) {
  const armed = useArmed()
  return (
    <button
      type="button"
      data-testid="handoff-save-replace"
      onClick={onReplace}
      disabled={submitting || !armed}
      className={`flex items-center gap-1.5 text-sm bg-amber-600 hover:bg-amber-500 text-white transition-colors disabled:opacity-50 ${shape}`}
    >
      {submitting && <Loader2 size={13} className="animate-spin" />}
      {label}
    </button>
  )
}
